import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Briefcase, FileText, Shield, ArrowRight, ArrowLeft, 
  CheckCircle, Loader2, Upload, User, Phone, Mail,
  GraduationCap, Building2, Users
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const FORM_STEPS = [
  { id: 'personal', title: 'Personal Info', icon: User },
  { id: 'contact', title: 'Contact', icon: Phone },
  { id: 'position', title: 'Position', icon: Briefcase },
  { id: 'education', title: 'Education', icon: GraduationCap },
  { id: 'experience', title: 'Experience', icon: Building2 },
  { id: 'references', title: 'References', icon: Users },
  { id: 'documents', title: 'Documents', icon: FileText },
  { id: 'review', title: 'Review', icon: CheckCircle },
];

export default function EmploymentApplicationPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    
    // Contact
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Position
    positionDesired: '',
    startDate: '',
    salaryExpected: '',
    employmentType: 'full-time',
    
    // Education
    highSchool: '',
    highSchoolGraduated: false,
    college: '',
    collegeDegree: '',
    collegeGraduated: false,
    otherEducation: '',
    
    // Experience
    employer1Name: '',
    employer1Address: '',
    employer1Phone: '',
    employer1Position: '',
    employer1StartDate: '',
    employer1EndDate: '',
    employer1Duties: '',
    employer1ReasonLeft: '',
    
    employer2Name: '',
    employer2Position: '',
    employer2StartDate: '',
    employer2EndDate: '',
    
    // References
    ref1Name: '',
    ref1Relationship: '',
    ref1Phone: '',
    ref1Email: '',
    
    ref2Name: '',
    ref2Relationship: '',
    ref2Phone: '',
    
    ref3Name: '',
    ref3Relationship: '',
    ref3Phone: '',
    
    // Documents
    resumeFile: null,
    
    // Agreements
    certifyAccurate: false,
    authorizeBackground: false,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, resumeFile: file }));
      toast.success(`File selected: ${file.name}`);
    }
  };

  const nextStep = () => {
    if (currentStep < FORM_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!formData.certifyAccurate || !formData.authorizeBackground) {
      toast.error('Please accept all required agreements');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload resume if present
      let resumeUrl = null;
      if (formData.resumeFile) {
        const uploadData = new FormData();
        uploadData.append('file', formData.resumeFile);
        const uploadRes = await fetch(`${API_BASE}/api/upload`, {
          method: 'POST',
          body: uploadData,
        });
        if (uploadRes.ok) {
          const uploadResult = await uploadRes.json();
          resumeUrl = uploadResult.url;
        }
      }

      // Submit application
      const submitData = {
        ...formData,
        resumeFile: undefined,
        resumeUrl,
        submittedAt: new Date().toISOString(),
      };

      const res = await fetch(`${API_BASE}/api/employment/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (res.ok) {
        toast.success('Application submitted successfully!');
        navigate('/employment/success', { state: { formType: 'application' } });
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (FORM_STEPS[currentStep].id) {
      case 'personal':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={formData.firstName} onChange={e => handleChange('firstName', e.target.value)} required />
              </div>
              <div>
                <Label>Middle Name</Label>
                <Input value={formData.middleName} onChange={e => handleChange('middleName', e.target.value)} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={formData.lastName} onChange={e => handleChange('lastName', e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Date of Birth *</Label>
                <Input type="date" value={formData.dateOfBirth} onChange={e => handleChange('dateOfBirth', e.target.value)} required />
              </div>
              <div>
                <Label>Social Security Number *</Label>
                <Input value={formData.ssn} onChange={e => handleChange('ssn', e.target.value)} placeholder="XXX-XX-XXXX" required />
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address *</Label>
                <Input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} required />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="(XXX) XXX-XXXX" required />
              </div>
            </div>
            <div>
              <Label>Street Address *</Label>
              <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>City *</Label>
                <Input value={formData.city} onChange={e => handleChange('city', e.target.value)} required />
              </div>
              <div>
                <Label>State *</Label>
                <Input value={formData.state} onChange={e => handleChange('state', e.target.value)} placeholder="WI" required />
              </div>
              <div>
                <Label>ZIP Code *</Label>
                <Input value={formData.zipCode} onChange={e => handleChange('zipCode', e.target.value)} required />
              </div>
            </div>
          </div>
        );

      case 'position':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Position Information</h2>
            <div>
              <Label>Position Desired *</Label>
              <Input value={formData.positionDesired} onChange={e => handleChange('positionDesired', e.target.value)} 
                     placeholder="e.g., Personal Care Worker, CNA, Office Staff" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Available Start Date *</Label>
                <Input type="date" value={formData.startDate} onChange={e => handleChange('startDate', e.target.value)} required />
              </div>
              <div>
                <Label>Expected Salary/Hourly Rate</Label>
                <Input value={formData.salaryExpected} onChange={e => handleChange('salaryExpected', e.target.value)} placeholder="e.g., $15/hr" />
              </div>
            </div>
            <div>
              <Label>Employment Type</Label>
              <div className="flex gap-4 mt-2">
                {['full-time', 'part-time', 'either'].map(type => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="employmentType" value={type} 
                           checked={formData.employmentType === type}
                           onChange={e => handleChange('employmentType', e.target.value)}
                           className="w-4 h-4" />
                    <span className="capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'education':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Education</h2>
            <Card className="p-4 bg-slate-50">
              <h3 className="font-semibold mb-3">High School</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>School Name</Label>
                  <Input value={formData.highSchool} onChange={e => handleChange('highSchool', e.target.value)} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox checked={formData.highSchoolGraduated} 
                            onCheckedChange={v => handleChange('highSchoolGraduated', v)} />
                  <Label>Graduated / GED</Label>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-50">
              <h3 className="font-semibold mb-3">College / University</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>School Name</Label>
                  <Input value={formData.college} onChange={e => handleChange('college', e.target.value)} />
                </div>
                <div>
                  <Label>Degree / Major</Label>
                  <Input value={formData.collegeDegree} onChange={e => handleChange('collegeDegree', e.target.value)} />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox checked={formData.collegeGraduated} 
                          onCheckedChange={v => handleChange('collegeGraduated', v)} />
                <Label>Graduated</Label>
              </div>
            </Card>
            <div>
              <Label>Other Education / Certifications</Label>
              <Input value={formData.otherEducation} onChange={e => handleChange('otherEducation', e.target.value)} 
                     placeholder="e.g., CNA Certification, CPR, First Aid" />
            </div>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Work Experience</h2>
            <Card className="p-4 bg-slate-50">
              <h3 className="font-semibold mb-3">Most Recent Employer</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Employer Name</Label>
                    <Input value={formData.employer1Name} onChange={e => handleChange('employer1Name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={formData.employer1Phone} onChange={e => handleChange('employer1Phone', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={formData.employer1Address} onChange={e => handleChange('employer1Address', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Position Held</Label>
                    <Input value={formData.employer1Position} onChange={e => handleChange('employer1Position', e.target.value)} />
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" value={formData.employer1StartDate} onChange={e => handleChange('employer1StartDate', e.target.value)} />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input type="date" value={formData.employer1EndDate} onChange={e => handleChange('employer1EndDate', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Job Duties</Label>
                  <Input value={formData.employer1Duties} onChange={e => handleChange('employer1Duties', e.target.value)} />
                </div>
                <div>
                  <Label>Reason for Leaving</Label>
                  <Input value={formData.employer1ReasonLeft} onChange={e => handleChange('employer1ReasonLeft', e.target.value)} />
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-50">
              <h3 className="font-semibold mb-3">Previous Employer (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Employer Name</Label>
                  <Input value={formData.employer2Name} onChange={e => handleChange('employer2Name', e.target.value)} />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input value={formData.employer2Position} onChange={e => handleChange('employer2Position', e.target.value)} />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'references':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Professional References</h2>
            <p className="text-slate-600">Please provide 3 professional references (not family members)</p>
            
            {[1, 2, 3].map(num => (
              <Card key={num} className="p-4 bg-slate-50">
                <h3 className="font-semibold mb-3">Reference {num}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name {num === 1 && '*'}</Label>
                    <Input value={formData[`ref${num}Name`]} 
                           onChange={e => handleChange(`ref${num}Name`, e.target.value)} />
                  </div>
                  <div>
                    <Label>Relationship {num === 1 && '*'}</Label>
                    <Input value={formData[`ref${num}Relationship`]} 
                           onChange={e => handleChange(`ref${num}Relationship`, e.target.value)}
                           placeholder="e.g., Former Supervisor" />
                  </div>
                  <div>
                    <Label>Phone {num === 1 && '*'}</Label>
                    <Input value={formData[`ref${num}Phone`]} 
                           onChange={e => handleChange(`ref${num}Phone`, e.target.value)} />
                  </div>
                  {num === 1 && (
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={formData.ref1Email} 
                             onChange={e => handleChange('ref1Email', e.target.value)} />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Documents & Agreements</h2>
            
            <Card className="p-6 bg-slate-50">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Upload className="w-5 h-5" /> Resume Upload (Optional)
              </h3>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} 
                       className="hidden" id="resume-upload" />
                <label htmlFor="resume-upload" className="cursor-pointer">
                  {formData.resumeFile ? (
                    <div className="text-green-600">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                      <p>{formData.resumeFile.name}</p>
                      <p className="text-sm text-slate-500">Click to change</p>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <FileText className="w-8 h-8 mx-auto mb-2" />
                      <p>Click to upload resume</p>
                      <p className="text-sm">PDF, DOC, DOCX (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-200">
              <h3 className="font-semibold mb-4">Required Agreements</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox checked={formData.certifyAccurate} 
                            onCheckedChange={v => handleChange('certifyAccurate', v)} />
                  <Label className="text-sm leading-relaxed">
                    I certify that all information provided in this application is accurate and complete. 
                    I understand that any false statements may result in rejection or termination. *
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox checked={formData.authorizeBackground} 
                            onCheckedChange={v => handleChange('authorizeBackground', v)} />
                  <Label className="text-sm leading-relaxed">
                    I authorize Avida Personal Care to conduct background checks and verify all information 
                    provided, including contacting previous employers and references. *
                  </Label>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Review Your Application</h2>
            
            <Card className="p-4">
              <h3 className="font-semibold text-amber-600 mb-2">Personal Information</h3>
              <p><strong>Name:</strong> {formData.firstName} {formData.middleName} {formData.lastName}</p>
              <p><strong>DOB:</strong> {formData.dateOfBirth}</p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-amber-600 mb-2">Contact</h3>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
              <p><strong>Address:</strong> {formData.address}, {formData.city}, {formData.state} {formData.zipCode}</p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-amber-600 mb-2">Position</h3>
              <p><strong>Desired Position:</strong> {formData.positionDesired}</p>
              <p><strong>Start Date:</strong> {formData.startDate}</p>
              <p><strong>Type:</strong> {formData.employmentType}</p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-amber-600 mb-2">Documents</h3>
              <p><strong>Resume:</strong> {formData.resumeFile ? formData.resumeFile.name : 'Not uploaded'}</p>
              <p><strong>Agreements:</strong> {formData.certifyAccurate && formData.authorizeBackground ? '✓ Accepted' : '⚠ Incomplete'}</p>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer group" onClick={() => navigate('/employment')}>
            <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-[#0A368F] transition-colors" />
            <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
          </div>
          <span className="badge-navy">Employment Application</span>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          {/* Progress bar */}
          <div className="progress-gradient mb-4">
            <div 
              className="h-full transition-all duration-500" 
              style={{ width: `${((currentStep + 1) / FORM_STEPS.length) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between overflow-x-auto gap-2 scrollbar-hide">
            {FORM_STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(idx)}
                  data-testid={`step-${step.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    isActive ? 'step-active shadow-lg shadow-blue-200' :
                    isCompleted ? 'step-completed' :
                    'step-pending hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{step.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="card-luxury p-8 md:p-10">
          {renderStepContent()}
        </div>
      </main>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-100 p-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto flex justify-between gap-4">
          <Button 
            variant="outline" 
            onClick={prevStep} 
            disabled={currentStep === 0}
            className="btn-outline-navy h-12 px-6"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          
          {currentStep === FORM_STEPS.length - 1 ? (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting} 
              className="btn-navy h-12 px-8"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" /> Submit Application</>
              )}
            </Button>
          ) : (
            <Button 
              onClick={nextStep} 
              className="btn-gold h-12 px-8"
              data-testid="next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
